import { useMemo, useRef, useState } from 'react';
import { NJCheckbox, NJIcon, NJInlineMessage, NJText } from '@engie-group/fluid-design-system-react';
import { UMInput } from '../../urbanmap/_components';
import { PlanSegmentedControl } from './PlanSegmentedControl';
import { Chip, chipListStyle } from './Chip';
import type { HeatNetwork, ProjectKind } from './types';

interface Props {
  kind: ProjectKind;
  onKindChange: (k: ProjectKind) => void;
  networks: HeatNetwork[];
  selected: string[];
  onSelectedChange: (ids: string[]) => void;
  ready: boolean;
}

const KIND_LABEL: Record<ProjectKind, string> = {
  new: 'Nouveau réseau',
  extension: 'Réseaux existants',
};

const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const label = (n: HeatNetwork) => `${n.code ?? 'Code non fourni'} — ${n.name ?? `Réseau sans nom (${n.id})`}`;
const operatorLabel = (n: HeatNetwork) => n.isEngie === null || n.isEngie === undefined ? 'Affiliation inconnue' : n.isEngie ? 'ENGIE' : 'Non-ENGIE';

/** Type de projet et, pour un développement, recherche des réseaux repris (code + nom). */
export function NetworkPicker({ kind, onKindChange, networks, selected, onSelectedChange, ready }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const byId = useMemo(() => new Map(networks.map((n) => [n.id, n])), [networks]);
  /** Seuls les réseaux intersectant le périmètre sont transmis par StepPerimeter. */
  const suggestions = useMemo(() => {
    const q = norm(query.trim());
    return networks
      .filter((n) => !q || norm(`${n.name ?? ''} ${n.code ?? ''} ${n.operator ?? ''} ${operatorLabel(n)}`).includes(q))
      .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id, 'fr'));
  }, [networks, query]);

  const toggle = (id: string) => {
    onSelectedChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);
  };

  if (kind === 'new') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <PlanSegmentedControl label="Type de projet" options={[KIND_LABEL.new, KIND_LABEL.extension]} value={KIND_LABEL.new} onChange={(v) => onKindChange(v === KIND_LABEL.extension ? 'extension' : 'new')} />
        <NJInlineMessage variant="information" scale="sm" hasStatus={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-8)' }}>
            <NJIcon name="add_circle" variant="information" scale="md" aria-hidden="true" style={{ flexShrink: 0 }} />
            <NJText scale="sm">Aucun réseau repris : le projet part d’une page blanche.</NJText>
          </div>
        </NJInlineMessage>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <PlanSegmentedControl label="Type de projet" options={[KIND_LABEL.new, KIND_LABEL.extension]} value={KIND_LABEL.extension} onChange={(v) => onKindChange(v === KIND_LABEL.extension ? 'extension' : 'new')} />

      <div
        style={{ position: 'relative' }}
        onFocus={() => { clearTimeout(blurTimer.current); setOpen(true); }}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120); }}
      >
        <fieldset disabled={!ready || networks.length === 0} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <UMInput
            value={query}
            onChange={(value) => { setQuery(value); setOpen(true); }}
            placeholder="Code ou nom de réseau…"
            ariaLabel="Rechercher un réseau de chaleur"
            icon="search"
          />
        </fieldset>
        {open && ready && networks.length > 0 && (
          <ul
            aria-label="Réseaux proposés"
            onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, margin: 0, padding: 0, listStyle: 'none',
              background: 'var(--nj-semantic-color-background-neutral-primary-default)',
              border: 'var(--nj-semantic-size-border-width) solid var(--nj-semantic-color-border-neutral-subtle-default)',
              borderRadius: 'var(--nj-semantic-size-border-radius-none)',
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {suggestions.length === 0 && <li style={{ padding: 'var(--nj-semantic-size-spacing-8)' }}><NJText scale="xs">Aucun réseau ne correspond à la recherche.</NJText></li>}
            {suggestions.map((n) => (
              <li key={n.id} style={{ padding: 'var(--nj-semantic-size-spacing-4) var(--nj-semantic-size-spacing-8)' }}>
                <NJCheckbox
                  inputId={`network-${n.id}`}
                  isChecked={selected.includes(n.id)}
                  onChange={() => toggle(n.id)}
                  ariaLabel={`${label(n)} · ${operatorLabel(n)}`}
                >
                  <NJText as="span" scale="xs">{`${label(n)} · ${operatorLabel(n)}`}</NJText>
                </NJCheckbox>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected.length > 0 && (
        <ul style={chipListStyle}>
          {selected.map((id) => {
            const n = byId.get(id);
            return (
              <Chip
                key={id}
                label={n ? label(n) : id}
                removeLabel={`Retirer ${n?.name ?? id}`}
                onRemove={() => onSelectedChange(selected.filter((x) => x !== id))}
              />
            );
          })}
        </ul>
      )}
      <NJText scale="sm">
        {!ready ? 'Sélectionnez d’abord une commune ou validez un contour.'
          : networks.length === 0 ? 'Aucun réseau des fichiers fournis ne traverse ce périmètre.'
            : `${networks.length} réseau(x) dans le périmètre · ${selected.length} retenu(s). Tous restent visibles sur la carte.`}
      </NJText>
    </div>
  );
}
